'use client';
import { useEffect, useLayoutEffect, useRef, useState, useCallback, memo } from 'react';

/* ============================================================
   SplitFlap — an authentic four-panel Solari cell.

   Each cell is four stacked halves inside a 3D `perspective`:
     .half.top / .half.bottom   static — show the CURRENT glyph
     .flap.top                  animated — INCOMING glyph, falls forward
     .flap.bottom               animated — INCOMING glyph, revealed under
   Each half renders the FULL glyph and clips to its 50%, so the two
   line up into one character split by the seam.

   The riffle (walking the charset from current→target, one card per
   step) is preserved from the original — it's the flap "click-through."
   JS decides WHICH glyph each fall lands on; CSS handles the fall.

   API (backward compatible with the original):
     text       string to display
     size       px font-size (cell size scales from this)
     className  extra classes on the row
   Extensions:
     width      fixed cell count; text is padded/truncated to it (board align)
     align      'left' | 'center' | 'right' when padding to width
     flipMs     per-card fall duration
     staggerMs  left-to-right per-cell start offset
   ============================================================ */

const CHARSET = " ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789:./-'&·";
const DIGITS = " 0123456789";  // digit reel: rank/score cells cycle only these
const FLIP_MS = 70;
const RIFFLE_GAP = 40;

/* ---------- one cell ----------
   Driven by a single rAF loop reading a target ref. There is exactly one
   animation clock per cell for its whole lifetime, so target changes can
   never interleave two riffles — the loop just re-reads the goal. This is
   what makes the flip robust under rapid updates. */
const Cell = memo(function Cell({ target, size, flipMs, staggerMs, index }) {
  const refs = useRef(null);
  const goalRef = useRef(' ');
  const startAtRef = useRef(0); // stagger: don't begin riffling until this time

  const setNode = useCallback((el) => {
    if (!el) return;
    refs.current = {
      staticTop: el.querySelector('.sf-half.sf-top .sf-g'),
      staticBot: el.querySelector('.sf-half.sf-bottom .sf-g'),
      flapTop: el.querySelector('.sf-flap.sf-top'),
      flapTopG: el.querySelector('.sf-flap.sf-top .sf-g'),
      flapBot: el.querySelector('.sf-flap.sf-bottom'),
      flapBotG: el.querySelector('.sf-flap.sf-bottom .sf-g'),
      shadow: el.querySelector('.sf-shadow'),
    };
  }, []);

  // set the goal whenever target changes; the persistent rAF loop picks it up
  useLayoutEffect(() => {
    goalRef.current = target.toUpperCase();
    startAtRef.current = performance.now() + index * staggerMs;
  }, [target, index, staggerMs]);

  // the one and only animation clock for this cell
  useEffect(() => {
    const r = refs.current;
    if (!r) return;
    const put = (node, ch) => { node.textContent = ch === ' ' ? '\u00A0' : ch; };
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let raf = 0;
    let current = ' ';
    // phase: 'idle' | 'falling'; when falling, `next` is the incoming glyph
    let phase = 'idle';
    let fallStart = 0;
    let next = ' ';
    let nextRiffleAt = 0;

    // paint both static halves to a glyph
    const paintStatic = (ch) => { put(r.staticTop, ch); put(r.staticBot, ch); };
    paintStatic(' ');

    const beginFall = (to, nowT) => {
      next = to;
      phase = 'falling';
      fallStart = nowT;
      put(r.flapTopG, to); put(r.flapBotG, to);
      paintStatic(current); // static still shows the old glyph beneath
      r.flapTop.style.transition = 'none';
      r.flapBot.style.transition = 'none';
      r.flapTop.style.transform = 'rotateX(0deg)';
      r.flapBot.style.transform = 'rotateX(90deg)';
      r.shadow.style.background = 'rgba(0,0,0,0)';
      void r.flapTop.offsetHeight;
      r.flapTop.style.transition = `transform ${flipMs}ms cubic-bezier(.4,.1,.3,1)`;
      r.flapBot.style.transition = `transform ${flipMs}ms cubic-bezier(.5,0,.2,1) ${flipMs * 0.55}ms`;
      r.shadow.style.transition = `background ${flipMs}ms linear`;
      r.flapTop.style.transform = 'rotateX(-90deg)';
      r.flapBot.style.transform = 'rotateX(0deg)';
      r.shadow.style.background = 'rgba(0,0,0,.28)';
    };

    const endFall = () => {
      current = next;
      paintStatic(current);
      r.flapTop.style.transition = 'none';
      r.flapBot.style.transition = 'none';
      r.flapTop.style.transform = 'rotateX(0deg)';
      r.flapBot.style.transform = 'rotateX(90deg)';
      r.shadow.style.transition = 'none';
      r.shadow.style.background = 'rgba(0,0,0,0)';
      phase = 'idle';
    };

    const tick = (nowT) => {
      const goal = goalRef.current;

      if (reduced) {
        if (current !== goal) { current = goal; paintStatic(goal); }
        raf = requestAnimationFrame(tick);
        return;
      }

      if (phase === 'falling') {
        if (nowT - fallStart >= flipMs + flipMs * 0.55) {
          endFall();
          nextRiffleAt = nowT + RIFFLE_GAP;
        }
      } else if (current !== goal && nowT >= startAtRef.current && nowT >= nextRiffleAt) {
        // advance one step toward goal along the RELEVANT reel. A real
        // Solari cell has one reel; a scoreboard digit cell shouldn't walk
        // the alphabet to reach "2". Pick the reel that contains the goal:
        // digits cycle DIGITS, letters+punct cycle the full charset. From a
        // blank/space start we jump onto the reel at its first glyph.
        const reel = DIGITS.indexOf(goal) !== -1 ? DIGITS : CHARSET;
        let idx = reel.indexOf(current);
        if (idx === -1) idx = 0; // current not on this reel (e.g. space) → start
        const inReel = reel.indexOf(goal) !== -1;
        const step = inReel ? reel[(idx + 1) % reel.length] : goal;
        beginFall(step, nowT);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [flipMs]);

  const cell = size;
  return (
    <span
      className="sf-cell"
      ref={setNode}
      aria-hidden="true"
      style={{ width: cell * 0.72, height: cell * 1.06, perspective: cell * 5.6 }}
    >
      <span className="sf-half sf-bottom"><span className="sf-g">&nbsp;</span></span>
      <span className="sf-half sf-top"><span className="sf-g">&nbsp;</span></span>
      <span className="sf-flap sf-bottom"><span className="sf-g">&nbsp;</span></span>
      <span className="sf-flap sf-top"><span className="sf-g">&nbsp;</span></span>
      <span className="sf-shadow" />
    </span>
  );
});

function fit(text, width, align) {
  const t = String(text).toUpperCase();
  if (width == null) return t;
  if (t.length >= width) return t.slice(0, width);
  const pad = width - t.length;
  if (align === 'right') return ' '.repeat(pad) + t;
  if (align === 'center') {
    const l = Math.floor(pad / 2);
    return ' '.repeat(l) + t + ' '.repeat(pad - l);
  }
  return t + ' '.repeat(pad); // left
}

export default function SplitFlap({
  text = '',
  size = 22,
  width = null,
  align = 'left',
  flipMs = FLIP_MS,
  staggerMs = 26,
  color = null,        // glyph color; null = default off-white. e.g. amber points
  className = '',
}) {
  const display = fit(text, width, align);
  const chars = display.split('');

  return (
    <span
      className={`sf-row ${className}`}
      style={{ fontSize: size, ...(color ? { '--sf-glyph': color } : null) }}
      aria-label={String(text)}
      role="text"
    >
      {chars.map((c, i) => (
        <Cell
          key={i}
          index={i}
          target={c}
          size={size}
          flipMs={flipMs}
          staggerMs={staggerMs}
        />
      ))}
      <style>{SF_CSS}</style>
    </span>
  );
}

/* scoped CSS — injected once per row; cheap and keeps the component drop-in */
const SF_CSS = `
.sf-row{
  display:inline-flex; gap:3px; padding:10px; border-radius:8px;
  background:#0a0b0d;
  box-shadow: inset 0 2px 6px rgba(0,0,0,.8), inset 0 -1px 0 rgba(255,255,255,.03),
    0 10px 30px -14px rgba(0,0,0,.9);
  line-height:1; font-weight:700;
  font-family: "Helvetica Neue", Arial, sans-serif;
  font-variant-numeric: tabular-nums;
}
.sf-cell{ position:relative; display:inline-block; }
.sf-half,.sf-flap{
  position:absolute; left:0; right:0; height:50%; overflow:hidden;
  background:#16181c; display:flex; justify-content:center;
}
.sf-g{
  position:absolute; left:0; right:0; height:200%;
  display:flex; align-items:center; justify-content:center; color:var(--sf-glyph, #f2f1ec);
}
.sf-half.sf-top,.sf-flap.sf-top{
  top:0; border-radius:5px 5px 0 0; align-items:flex-end;
  background:linear-gradient(180deg,#1d2025,#16181c);
  border-bottom:1px solid #05060a;
}
.sf-half.sf-top .sf-g,.sf-flap.sf-top .sf-g{ top:0; }
.sf-half.sf-bottom,.sf-flap.sf-bottom{
  bottom:0; border-radius:0 0 5px 5px; align-items:flex-start;
  background:linear-gradient(180deg,#16181c,#121417);
  border-top:1px solid rgba(255,255,255,.04);
}
.sf-half.sf-bottom .sf-g,.sf-flap.sf-bottom .sf-g{ bottom:0; }
/* image cells: the img is the "glyph" — full cell height, clipped per half */
.sf-cell-img .sf-half,.sf-cell-img .sf-flap{ justify-content:center; }
.sf-img{
  position:absolute; left:50%; transform:translateX(-50%);
  height:200%; width:auto; object-fit:cover; display:block;
}
.sf-half.sf-top .sf-img,.sf-flap.sf-top .sf-img{ top:0; }
.sf-half.sf-bottom .sf-img,.sf-flap.sf-bottom .sf-img{ bottom:0; }
.sf-flap.sf-top{
  transform-origin:bottom center; transform:rotateX(0deg); z-index:3;
  backface-visibility:hidden;
  background-image:linear-gradient(180deg,rgba(255,255,255,.06),rgba(0,0,0,0) 40%),
    linear-gradient(180deg,#1d2025,#16181c);
}
.sf-flap.sf-bottom{
  transform-origin:top center; transform:rotateX(90deg); z-index:2;
  backface-visibility:hidden;
}
.sf-shadow{
  position:absolute; left:0; right:0; top:50%; height:50%;
  background:rgba(0,0,0,0); z-index:4; pointer-events:none;
  border-radius:0 0 5px 5px;
}
@media (prefers-reduced-motion: reduce){
  .sf-flap.sf-top,.sf-flap.sf-bottom{ transition:none !important; }
}
`;
